"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { SITE, WEB_APP_LINKS } from "@/lib/constants";
import { AppStoreBadges } from "@/components/shared/AppStoreBadges";

const ease = [0.25, 0.1, 0.25, 1] as const;

export function Hero() {
  return (
    <section className="relative overflow-hidden pb-16 pt-12 sm:pb-24 sm:pt-20">
      {/* V9 rebrand — the brand gradient (share cards / store slides family):
          the funnel is card → site → listing, all on the same dark navy. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2447] via-[#24549E] to-[#5D5BD5]" />
        <div className="absolute bottom-0 left-1/2 h-[600px] w-[900px] -translate-x-1/2 translate-y-[20%] rounded-full bg-[radial-gradient(ellipse,rgba(120,170,255,0.25),transparent_70%)] blur-2xl" />
      </div>

      <div className="relative mx-auto flex max-w-7xl flex-col items-center px-4 sm:px-6">
        {/* Text — centered for more impact. Headline and subtitle render
            visible in the server HTML, with no entrance animation: the
            subtitle is the mobile LCP element, and an opacity-0 start made
            LCP wait for JS hydration plus the animation delay. */}
        <div className="max-w-3xl text-center">
          <h1 className="text-balance text-5xl font-bold leading-[1.08] tracking-tight text-white sm:text-7xl lg:text-8xl">
            Boost your brain
            <br />
            <span className="text-[#B9C8FF]">
              with science
            </span>
          </h1>
        </div>

        <p className="mt-6 max-w-xl text-center text-xl leading-relaxed text-white/85 sm:text-2xl">
          {SITE.description}
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55, ease }}
          className="mt-8 scroll-mt-[35vh]"
          id="download"
        >
          <AppStoreBadges placement="hero" size="lg" />
          <p className="mt-4 text-center text-base text-white/75">
            Free to download. Guided plan free for 7 days.
          </p>
          <p className="mt-1 flex flex-wrap justify-center gap-x-5 gap-y-1 text-center text-base text-white/75">
            <a
              href={WEB_APP_LINKS.signUp}
              className="underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              Or use Helibrain in your browser →
            </a>
            <Link
              href="/tests"
              className="underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              Try a free brain test →
            </Link>
          </p>
        </motion.div>

        {/* Phone mockup — large, centered, floating over gradient */}
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease }}
          className="relative mt-12 sm:mt-16"
        >
          {/* Glow behind phone */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,hsl(217_91%_60%/0.18),transparent_60%)] blur-3xl" />

          <div
            className="relative w-[300px] sm:w-[360px] lg:w-[420px]"
          >
            <Image
              src="/images/screenshots/brain-score.png"
              alt="Helibrain app showing daily Brain Score dashboard"
              width={730}
              height={1461}
              sizes="(min-width: 1024px) 420px, (min-width: 640px) 360px, 300px"
              priority
              className="drop-shadow-2xl"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
